import { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { brl, PAYMENT } from "@/lib/format";

const PERIODS = [7, 30, 90];
const COLORS = { dinheiro: "#10B981", pix: "#FF8A66", cartao: "#FF5A1F" };

const Kpi = ({ label, value, testid, accent }) => (
  <div className="rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5">
    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</p>
    <p data-testid={testid} className={`mt-2 font-display text-2xl font-bold ${accent || ""}`}>{value}</p>
  </div>
);

const tip = { contentStyle: { background: "#1A1A1A", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12 }, formatter: (v) => brl(v) };

export default function Dashboard() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/finance", { params: { days } }).then((r) => setData(r.data)); }, [days]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Visão geral</p>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Fluxo de caixa</h1>
        </div>
        <div className="flex gap-1 rounded-full bg-white/5 p-1">
          {PERIODS.map((p) => (
            <button key={p} data-testid={`period-${p}`} onClick={() => setDays(p)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium ${days === p ? "bg-primo-500 text-white" : "text-slate-400"}`}>{p} dias</button>
          ))}
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Faturamento hoje" value={brl(data.today_revenue)} testid="kpi-today-revenue" accent="text-primo-400" />
            <Kpi label={`Faturamento ${days}d`} value={brl(data.revenue)} testid="kpi-revenue" />
            <Kpi label="Pedidos no período" value={data.orders} testid="kpi-orders" />
            <Kpi label="Ticket médio" value={brl(data.avg_ticket)} testid="kpi-avg-ticket" />
            <Kpi label="Pedidos hoje" value={data.today_orders} testid="kpi-today-orders" />
            <Kpi label="Taxas de entrega" value={brl(data.delivery_fees)} testid="kpi-delivery-fees" />
            <Kpi label="Estoque baixo (≤5)" value={data.low_stock} testid="kpi-low-stock" accent={data.low_stock ? "text-red-400" : ""} />
            <Kpi label={`Descontos ${days}d`} value={brl(data.discounts)} testid="kpi-discounts" accent={data.discounts ? "text-red-400" : ""} />
          </div>

          <div className="rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5">
            <h2 className="mb-4 font-display text-lg font-semibold">Faturamento por dia</h2>
            <div className="h-64" data-testid="chart-by-day">
              <ResponsiveContainer>
                <AreaChart data={data.by_day.map((d) => ({ ...d, label: d.date.slice(8, 10) + "/" + d.date.slice(5, 7) }))}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF5A1F" stopOpacity={0.4} /><stop offset="100%" stopColor="#FF5A1F" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="rgba(255,255,255,.05)" vertical={false} />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} width={50} />
                  <Tooltip {...tip} />
                  <Area dataKey="total" name="Faturamento" stroke="#FF5A1F" strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5">
              <h2 className="mb-4 font-display text-lg font-semibold">Por forma de pagamento</h2>
              <div className="flex items-center gap-6">
                <div className="h-44 w-44 shrink-0">
                  <ResponsiveContainer>
                    <PieChart><Pie data={data.by_payment} dataKey="total" nameKey="method" innerRadius={45} outerRadius={75} stroke="none">
                      {data.by_payment.map((p) => <Cell key={p.method} fill={COLORS[p.method]} />)}
                    </Pie><Tooltip {...tip} /></PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3 text-sm">
                  {data.by_payment.map((p) => (
                    <div key={p.method} data-testid={`payment-total-${p.method}`} className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[p.method] }} />
                      <span className="text-slate-400">{PAYMENT[p.method]}</span>
                      <span className="font-semibold">{brl(p.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5">
              <h2 className="mb-4 font-display text-lg font-semibold">Bebidas mais vendidas</h2>
              {data.by_product.length === 0 && <p className="text-sm text-slate-500">Sem vendas no período.</p>}
              <div className="space-y-3" data-testid="top-products">
                {data.by_product.map((p) => (
                  <div key={p.name}>
                    <div className="flex justify-between text-sm"><span className="truncate pr-2">{p.qty}x {p.name}</span><span className="font-semibold">{brl(p.total)}</span></div>
                    <div className="mt-1 h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-primo-500" style={{ width: `${(p.total / data.by_product[0].total) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5">
            <h2 className="mb-4 font-display text-lg font-semibold">Cupons e parcerias no período</h2>
            {data.by_coupon.length === 0 && <p className="text-sm text-slate-500">Nenhum cupom usado no período.</p>}
            <div className="space-y-2" data-testid="coupon-usage">
              {data.by_coupon.map((c) => (
                <div key={c.code} data-testid={`finance-coupon-${c.code}`} className="flex items-center justify-between rounded-xl bg-black/30 px-3 py-2 text-sm">
                  <span className="font-semibold tracking-wide">{c.code}{c.partner ? <span className="ml-2 text-xs text-slate-500">{c.partner}</span> : null}</span>
                  <span className="text-slate-400">{c.uses} uso(s) · <span className="text-red-400">-{brl(c.discount)}</span></span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
